// Coverage line query + mutation hooks — FE-SCRM-015.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  addCoverageLine,
  deleteCoverageLine,
  fetchCoverageLines,
  updateCoverageLine,
} from "./api";
import type {
  AddCoverageLineInput,
  CoverageLine,
  CoverageLineListResponse,
  UpdateCoverageLineInput,
} from "./types";


export const COVERAGE_LINES_KEY = "sales-coverage-lines" as const;


export function useCoverageLinesQuery(
  opportunityId: string | undefined,
): UseQueryResult<CoverageLineListResponse> {
  return useQuery({
    queryKey: [COVERAGE_LINES_KEY, opportunityId],
    queryFn: ({ signal }) =>
      fetchCoverageLines(opportunityId!, { signal }),
    enabled: Boolean(opportunityId),
  });
}


function _invalidate(
  qc: ReturnType<typeof useQueryClient>, opportunityId: string,
) {
  qc.invalidateQueries({ queryKey: [COVERAGE_LINES_KEY, opportunityId] });
}


export function useAddCoverageLineMutation(
  opportunityId: string,
): UseMutationResult<CoverageLine, Error, AddCoverageLineInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input) => addCoverageLine(opportunityId, input),
    onSuccess: () => _invalidate(qc, opportunityId),
  });
}


export interface UpdateCoverageLineVariables {
  coverage_line_id: string;
  values: UpdateCoverageLineInput;
}


export function useUpdateCoverageLineMutation(
  opportunityId: string,
): UseMutationResult<CoverageLine, Error, UpdateCoverageLineVariables> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) => updateCoverageLine(vars.coverage_line_id, vars.values),
    onSuccess: () => _invalidate(qc, opportunityId),
  });
}


export function useDeleteCoverageLineMutation(
  opportunityId: string,
): UseMutationResult<{ deleted: boolean }, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (lineId: string) => deleteCoverageLine(lineId),
    onSuccess: () => _invalidate(qc, opportunityId),
  });
}
